// src/contexts/AuthContext.tsx
import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import * as authService from '../services/authService';
import { useNotification } from './NotificationContext';
import { supabase } from '../supabaseClient';

// ========== TYPES ==========
interface AuthUser {
  id: string;
  email: string | null;
}

interface ProfileData {
  id: string;
  role: string;
  plan: string;
  company_code?: string;
  subscription_status?: string;
  trial_started_at?: string;
  trial_ends_at?: string;
  carrier_limit?: number;
  first_name?: string;
  last_name?: string;
}

interface AuthContextType {
  currentUser: AuthUser | null;
  profile: ProfileData | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: any) => Promise<any>;
  logout: () => void;
  isAuthenticated: boolean;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

interface Props {
  children: ReactNode;
}

// ========== PROVIDER ==========
export const AuthProvider = ({ children }: Props) => {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const { addNotification } = useNotification();

  // ========== FETCH PROFILE ==========
  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, role, plan, company_code, subscription_status, trial_started_at, trial_ends_at, carrier_limit, first_name, last_name")
      .eq("id", userId)
      .single();

    if (!error && data) {
      setProfile(data);
      return data;
    }
    return null;
  };

  // ========== INIT SESSION ==========
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      const session = data.session;
      if (session?.user) {
        const { id, email } = session.user;
        setCurrentUser({ id, email: email ?? null });
        setIsAuthenticated(true);
        await fetchProfile(id);
      }
      setLoading(false);
    });

    supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        setCurrentUser(null);
        setProfile(null);
        setIsAuthenticated(false);
      }
    });
  }, []);

  // ======= LOGIN =======
  const login = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      if (data.user) {
        const { id, email } = data.user;

        setCurrentUser({ id, email: email ?? null });
        setIsAuthenticated(true);

        const prof = await fetchProfile(id);
        console.log('🔍 Profile fetched:', prof);
        
        // Force synchronous state upd
        flushSync(() => {
          setProfile(prof);
        });
        
        console.log('✅ Profile state updated');
        // Navigation will be handled by AuthRedirect component

        addNotification("success", "Login successful!");
      }
    } catch (err: any) {
      setError(err.message);
      addNotification("error", err.message || "Login failed.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ========== REGISTER ==========
  const register = async (userData: any) => {
    try {
      setError(null);
      setLoading(true);

      const response = await authService.register(userData);

      addNotification(
        "success",
        "Registration successful! Please verify your email."
      );

      return response;
    } catch (err: any) {
      setError(err.message);
      addNotification("error", err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ========== LOGOUT ==========
  const logout = async () => {
    await supabase.auth.signOut();
    setCurrentUser(null);
    setProfile(null);
    setIsAuthenticated(false);

    addNotification("info", "You have been logged out.");
    navigate("/login");
  };

  const clearError = () => setError(null);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        profile,
        loading,
        error,
        login,
        register,
        logout,
        isAuthenticated,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;